import * as tf from '@tensorflow/tfjs';

// Network architecture
const INPUT_SIZE = 10;
const HIDDEN_SIZE = 64;
const OUTPUT_SIZE = 5;
const LEARNING_RATE = 0.001;
const EPSILON = 0.2;

// Create the policy network
export function createPolicyNetwork() {
  const model = tf.sequential();
  model.add(tf.layers.dense({ 
    units: HIDDEN_SIZE, 
    activation: 'relu', 
    inputShape: [INPUT_SIZE] 
  }));
  model.add(tf.layers.dense({ 
    units: HIDDEN_SIZE, 
    activation: 'relu' 
  }));
  model.add(tf.layers.dense({ 
    units: OUTPUT_SIZE, 
    activation: 'softmax' 
  }));
  
  model.compile({
    optimizer: tf.train.adam(LEARNING_RATE),
    loss: 'categoricalCrossentropy'
  });
  
  return model;
}

// Select action using the policy network
export async function selectAction(state: number[]): Promise<{ action: number; logProb: number }> {
  return tf.tidy(() => {
    const stateTensor = tf.tensor2d([state]);
    const actionProbs = model.predict(stateTensor) as tf.Tensor;
    const actionData = actionProbs.dataSync();
    
    // Sample action using probabilities
    let cumulative = 0;
    const rand = Math.random();
    let selectedAction = 0;
    
    for (let i = 0; i < actionData.length; i++) {
      cumulative += actionData[i];
      if (rand < cumulative) {
        selectedAction = i;
        break;
      }
    }
    
    const logProb = Math.log(actionData[selectedAction] + 1e-8);
    return { action: selectedAction, logProb };
  });
}

// Train the policy network using PPO
export async function trainPPO(experiences: Experience[]) {
  const batchSize = experiences.length;
  
  // Convert experiences to tensors
  const states = tf.tensor2d(experiences.map(exp => exp.state));
  const actions = tf.tensor1d(experiences.map(exp => exp.action), 'int32');
  const rewards = tf.tensor1d(experiences.map(exp => exp.reward));
  const oldLogProbs = tf.tensor1d(experiences.map(exp => exp.oldLogProb));
  
  // Training loop
  for (let epoch = 0; epoch < 5; epoch++) {
    tf.tidy(() => {
      // Forward pass
      const actionProbs = model.predict(states) as tf.Tensor;
      const newLogProbs = tf.logSoftmax(actionProbs).gather(actions, 1);
      
      // Calculate ratio and surrogate loss
      const ratio = tf.exp(newLogProbs.sub(oldLogProbs));
      const surrogate1 = ratio.mul(rewards);
      const surrogate2 = tf.clipByValue(ratio, 1 - EPSILON, 1 + EPSILON).mul(rewards);
      
      // PPO loss
      const loss = tf.neg(tf.minimum(surrogate1, surrogate2)).mean();
      
      // Backward pass
      const grads = tf.variableGrads(() => loss);
      optimizer.applyGradients(grads.grads);
    });
  }
  
  // Clean up tensors
  states.dispose();
  actions.dispose();
  rewards.dispose();
  oldLogProbs.dispose();
}

// Initialize model and optimizer
const model = createPolicyNetwork();
const optimizer = tf.train.adam(LEARNING_RATE);

// Export types
export interface Experience {
  state: number[];
  action: number;
  reward: number;
  oldLogProb: number;
}

// Process message to state vector
export function processMessageToState(message: string): number[] {
  const features = [
    message.length / 1000, // Normalized length
    message.split(' ').length / 100, // Word count
    message.split('?').length - 1, // Question marks
    (message.match(/\b(how|why|what|when|where|who)\b/gi) || []).length, // Question words
    (message.match(/\b(explain|describe|analyze|compare|evaluate)\b/gi) || []).length, // Analysis words
    (message.match(/\b(math|calculate|equation|number)\b/gi) || []).length, // Math indicators
    (message.match(/\b(money|finance|invest|budget)\b/gi) || []).length, // Finance indicators
    (message.match(/\b(science|physics|chemistry|biology)\b/gi) || []).length, // Science indicators
    message.includes('?') ? 1 : 0, // Is question
    message.toLowerCase().includes('help') ? 1 : 0 // Help request
  ];
  
  return features;
}